/**
 * scene-transition — the playful journey between scenes.
 *
 * No "page change": when an object is tapped, a circle of its color
 * bursts from the object and covers the screen; navigation happens behind and
 * the destination scene enters with its `screen-enter`. With prefers-reduced-motion
 * the journey is instant (no burst). The launch is idempotent while
 * a journey is in progress: repeated taps do not queue navigations.
 */

import { createSignal, type JSX, onCleanup, Show } from "solid-js";
import { prefersReducedMotion, TOY_LAUNCH_MS } from "@/lib/toy/motion";

interface BurstState {
  x: number;
  y: number;
  color: string;
  scale: number;
}

interface SceneLaunch {
  /** Launches the journey: burst from the tapped object and then `go()`. */
  launch: (event: MouseEvent, color: string, go: () => void) => void;
  /** Burst layer — mount once at the end of the scene. */
  BurstLayer: () => JSX.Element;
}

/** Base diameter of the burst circle (px) — scaled until it covers. */
const BURST_BASE_PX = 24;

export function createSceneLaunch(): SceneLaunch {
  const [burst, setBurst] = createSignal<BurstState | null>(null);
  let timer: ReturnType<typeof setTimeout> | undefined;

  onCleanup(() => {
    clearTimeout(timer);
  });

  const launch = (event: MouseEvent, color: string, go: () => void) => {
    if (burst()) {
      return;
    }
    if (prefersReducedMotion()) {
      go();
      return;
    }
    // Center of the tapped object (stable with keyboard and imprecise taps);
    // if there is no measurable box, the tap point.
    let x = event.clientX;
    let y = event.clientY;
    const target = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const rect = target?.getBoundingClientRect();
    if (rect && rect.width > 0) {
      x = rect.left + rect.width / 2;
      y = rect.top + rect.height / 2;
    }
    const vw = globalThis.innerWidth;
    const vh = globalThis.innerHeight;
    const radius = Math.hypot(Math.max(x, vw - x), Math.max(y, vh - y));
    setBurst({ x, y, color, scale: ((radius * 2) / BURST_BASE_PX) * 1.1 });
    timer = setTimeout(go, TOY_LAUNCH_MS);
  };

  const BurstLayer = (): JSX.Element => (
    <Show when={burst()}>
      {(b) => (
        <div class="toy-burst-layer" aria-hidden="true">
          <div
            class="toy-burst"
            style={{
              left: `${b().x}px`,
              top: `${b().y}px`,
              background: b().color,
              "--burst-scale": String(b().scale),
            }}
          />
        </div>
      )}
    </Show>
  );

  return { launch, BurstLayer };
}
